/**
 * Admin Merchant Tax Withholding API
 * Base: /api/v1/admin/merchant-tax
 */
import api from './api';

const BASE = '/admin/merchant-tax';

export type TaxCategory = 'goods_with_transport' | 'custom' | 'exempt';
export type BusinessClassification =
  | 'household'
  | 'individual'
  | 'enterprise'
  | 'unknown';

export interface MerchantTaxSettings {
  id: number | null;
  restaurantId: number;
  restaurantName?: string | null;
  withholdingEnabled: boolean;
  taxCategory: TaxCategory;
  vatRate: number;
  pitRate: number;
  totalRate: number;
  businessClassification: BusinessClassification | null;
  taxId: string | null;
  ownerFullName: string | null;
  notes: string | null;
  isDefault?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface TaxWithholdingRow {
  id: number;
  merchantId: number;
  restaurantName: string | null;
  orderId: number;
  orderNumber: string | null;
  orderTotal: number | null;
  paymentMethod: string | null;
  paymentStatus: string | null;
  orderStatus: string | null;
  paidAt: string | null;
  deliveredAt: string | null;
  platformFeeAmount: number | null;
  merchantNetAmount: number | null;
  taxBaseVnd: number;
  vatRate: number;
  vatAmountVnd: number;
  pitRate: number;
  pitAmountVnd: number;
  totalTaxVnd: number;
  status: 'WITHHELD' | 'REVERSED';
  periodYear: number;
  periodMonth: number;
  periodQuarter: number;
  withheldAt: string;
  reversedAt: string | null;
  reverseReason: string | null;
  remittanceStatus: 'PENDING' | 'REMITTED';
  remittedAt: string | null;
  remittanceNote: string | null;
}

export interface TaxPeriod {
  year: number | null;
  month: number | null;
  quarter: number | null;
}

export interface TaxSummary {
  orderCount: number;
  taxBaseVnd: number;
  vatAmountVnd: number;
  pitAmountVnd: number;
  totalTaxVnd: number;
  pendingRemittanceCount: number;
  remittedCount: number;
  pendingRemittanceVnd: number;
  remittedVnd: number;
  taxableRevenueVnd?: number;
  vatPayableVnd?: number;
  pitPayableVnd?: number;
  totalWithheldVnd?: number;
}

export interface TaxMerchantAggregate {
  merchantId: number;
  restaurantName: string | null;
  orderCount: number;
  taxBaseVnd: number;
  vatAmountVnd: number;
  pitAmountVnd: number;
  totalTaxVnd: number;
  pendingRemittanceVnd: number;
}

export interface DeclarationSummary {
  period: TaxPeriod;
  regulation: {
    label: string;
    vatRate: number;
    pitRate: number;
    totalRate: number;
    note: string;
  };
  summary: TaxSummary;
  merchants: TaxMerchantAggregate[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

function unwrapData<T>(res: {
  data?:
    | {
        data?: T;
        pagination?: Pagination;
        summary?: TaxSummary;
        period?: TaxPeriod;
      }
    | T;
}): {
  data: T;
  pagination?: Pagination;
  summary?: TaxSummary;
  period?: TaxPeriod;
} {
  const body = res.data as any;
  if (body && typeof body === 'object' && 'data' in body) {
    return {
      data: body.data as T,
      pagination: body.pagination,
      summary: body.summary,
      period: body.period,
    };
  }
  return { data: body as T };
}

export async function fetchMerchantTaxSettingsList(params: {
  q?: string;
  withholdingEnabled?: boolean;
  page?: number;
  limit?: number;
}): Promise<{ data: MerchantTaxSettings[]; pagination: Pagination }> {
  const res = await api.get(`${BASE}/settings`, { params });
  const { data, pagination } = unwrapData<MerchantTaxSettings[]>(res);
  return {
    data: data || [],
    pagination: pagination || { page: 1, limit: 20, total: 0, totalPages: 1 },
  };
}

export async function fetchMerchantTaxSettings(
  merchantId: number
): Promise<MerchantTaxSettings> {
  const res = await api.get(`${BASE}/settings/${merchantId}`);
  const { data } = unwrapData<MerchantTaxSettings>(res);
  return data;
}

export async function updateMerchantTaxSettings(
  merchantId: number,
  body: Partial<{
    withholdingEnabled: boolean;
    taxCategory: TaxCategory;
    vatRate: number;
    pitRate: number;
    businessClassification: BusinessClassification | null;
    taxId: string | null;
    ownerFullName: string | null;
    notes: string | null;
  }>
): Promise<MerchantTaxSettings> {
  const res = await api.put(`${BASE}/settings/${merchantId}`, body);
  const { data } = unwrapData<MerchantTaxSettings>(res);
  return data;
}

export async function fetchTaxWithholdings(params: {
  merchantId?: number;
  year?: number;
  month?: number;
  quarter?: number;
  period?: 'month' | 'quarter' | 'year';
  status?: 'WITHHELD' | 'REVERSED';
  remittanceStatus?: 'PENDING' | 'REMITTED';
  page?: number;
  limit?: number;
}): Promise<{
  data: TaxWithholdingRow[];
  pagination: Pagination;
  summary?: TaxSummary;
  period?: TaxPeriod;
}> {
  const res = await api.get(`${BASE}/withholdings`, { params });
  const unwrapped = unwrapData<TaxWithholdingRow[]>(res);
  return {
    data: unwrapped.data || [],
    pagination: unwrapped.pagination || {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 1,
    },
    summary: unwrapped.summary,
    period: unwrapped.period,
  };
}

export async function fetchTaxDeclaration(params: {
  merchantId?: number;
  year?: number;
  month?: number;
  quarter?: number;
  period?: 'month' | 'quarter' | 'year';
  page?: number;
  limit?: number;
}): Promise<DeclarationSummary> {
  const res = await api.get(`${BASE}/declaration`, { params });
  const { data } = unwrapData<DeclarationSummary>(res);
  return data;
}

export async function markTaxRemitted(body: {
  ids?: number[];
  merchantId?: number;
  year?: number;
  month?: number;
  quarter?: number;
  note?: string;
}): Promise<{ updated: number }> {
  const res = await api.post(`${BASE}/remit`, body);
  const { data } = unwrapData<{ updated: number }>(res);
  return data;
}

export async function fetchTaxStats(): Promise<{ withheldOrderCount: number }> {
  const res = await api.get(`${BASE}/stats`);
  const { data } = unwrapData<{ withheldOrderCount: number }>(res);
  return data;
}

export async function backfillTaxWithholdings(body?: {
  dryRun?: boolean;
  batchSize?: number;
  maxOrders?: number;
}): Promise<{
  processed: number;
  backfilled: number;
  skipped: number;
  errors: number;
  dryRun?: boolean;
}> {
  const res = await api.post(`${BASE}/backfill`, body || {});
  const { data } = unwrapData<{
    processed: number;
    backfilled: number;
    skipped: number;
    errors: number;
    dryRun?: boolean;
  }>(res);
  return data;
}

function parseFilenameFromDisposition(header?: string): string | null {
  if (!header) return null;
  const match = /filename="([^"]+)"/i.exec(header);
  return match?.[1] || null;
}

export async function downloadTaxDeclaration(
  params: {
    merchantId?: number;
    year?: number;
    month?: number;
    quarter?: number;
    period?: 'month' | 'quarter' | 'year';
    from?: string;
    to?: string;
  },
  format: 'csv' | 'pdf' | 'html'
): Promise<void> {
  const res = await api.get(`${BASE}/export`, {
    params: { ...params, format },
    responseType: 'blob',
  });
  const blob = res.data as Blob;
  const filename =
    parseFilenameFromDisposition(res.headers['content-disposition']) ||
    `to-khai-khau-tru-thue.${format === 'html' ? 'html' : format}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
